/**
 * useAttributeRuleEngine Hook
 *
 * React hook wrapper for AttributeRuleEngine
 * Provides access to rule engine instance for components
 */

import { useMemo } from "react"
import { AttributeRuleEngine } from "../engine"
import type { AttributeRuleMap } from "../types"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { FILLING_RULES } from "../configs/filling.config"
import { MAINTENANCE_RULES } from "../configs/maintaince.config"
import { MOVEMENT_RULES } from "../configs/movement.config"
import { FULFILMENT_RULES } from "../configs/fulfilment.config"
import { HOLD_RULES } from "../configs"

/**
 * Create complete rule map with all activity types
 * Currently Filling, Movement, and Maintenance are implemented
 */
const createRuleMap = (): AttributeRuleMap => {
  return {
    [gqlTypes.ActivityKind.Filling]: FILLING_RULES,
    [gqlTypes.ActivityKind.Movement]: MOVEMENT_RULES,
    [gqlTypes.ActivityKind.Maintenance]: MAINTENANCE_RULES,
    [gqlTypes.ActivityKind.Inspection]: MAINTENANCE_RULES,
    [gqlTypes.ActivityKind.Fulfilment]: FULFILMENT_RULES,
    [gqlTypes.ActivityKind.Hold]: HOLD_RULES,
  }
}

/**
 * Hook to access attribute rule engine
 *
 * Returns a memoized instance of AttributeRuleEngine with current rules
 *
 * @returns AttributeRuleEngine instance
 *
 * @example
 * ```tsx
 * const engine = useAttributeRuleEngine()
 * const visibleAttrs = engine.getVisibleAttributes(activityKind, eventKind)
 * ```
 */
export function useAttributeRuleEngine(): AttributeRuleEngine {
  const engine = useMemo(() => {
    const ruleMap = createRuleMap()
    return new AttributeRuleEngine(ruleMap)
  }, [])

  return engine
}
